/* =========================================================================
   Fit Guide
   Vanilla JS. No dependencies.
   Reads pre-rendered JSON blob, manages tab/filter/selection state,
   updates product card on shape click.
   Handles list metafields (arrays) and single-line text metafields (strings).
   ========================================================================= */

(function () {
  'use strict';

  function initFitGuide(root) {
    if (!root || root.dataset.fitGuideInitialized === 'true') return;
    root.dataset.fitGuideInitialized = 'true';

    // -- Data -------------------------------------------------------------

    const dataEl = root.querySelector('[data-fit-guide-data]');
    if (!dataEl) {
      console.error('Fit Guide: data element not found');
      return;
    }

    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('Fit Guide: failed to parse data blob', e);
      return;
    }

    const shapes = data.shapes || [];
    const shapesById = Object.fromEntries(shapes.map(s => [s.id, s]));

    // -- State ------------------------------------------------------------

    const state = {
      tab: 'tops',
      filters: new Set(),
      selectedId: null
    };

    // -- DOM refs ---------------------------------------------------------

    const tabs = root.querySelectorAll('[data-tab]');
    const filterGroups = root.querySelectorAll('[data-filter-for]');
    const tracks = root.querySelectorAll('[data-track]');
    const prevBtn = root.querySelector('[data-carousel-prev]');
    const nextBtn = root.querySelector('[data-carousel-next]');

    const card = {
      image:         root.querySelector('[data-card-image]'),
      title:         root.querySelector('[data-card-title]'),
      description:   root.querySelector('[data-card-description]'),
      features:      root.querySelector('[data-card-features]'),
      featuresWrap:  root.querySelector('[data-card-features-wrap]'),
      coverage:      root.querySelector('[data-card-coverage]'),
      coverageWrap:  root.querySelector('[data-card-coverage-wrap]'),
      cupsizes:      root.querySelector('[data-card-cupsizes]'),
      cupsizesWrap:  root.querySelector('[data-card-cupsizes-wrap]'),
      support:       root.querySelector('[data-card-support]'),
      supportWrap:   root.querySelector('[data-card-support-wrap]'),
      cta:           root.querySelector('[data-card-cta]')
    };

    // -- Helpers ----------------------------------------------------------

    // Normalize metafield value to display string.
    // Handles null, string, or array (from list metafields).
    function fieldToString(val) {
      if (val == null) return '';
      if (Array.isArray(val)) return val.filter(Boolean).join(', ');
      return String(val);
    }

    // Convert metafield value to array of items for feature lists.
    // List metafield returns array, multi-line text returns string to split.
    function fieldToItems(val) {
      if (val == null) return [];
      if (Array.isArray(val)) return val.map(v => String(v));
      return String(val).split(/\r?\n/);
    }

    function activeTrack() {
      return root.querySelector('[data-track="' + state.tab + '"]');
    }

    function shapesInTab(tab) {
      return shapes.filter(s => s.category === tab);
    }

    // Build lowercase token list from a field for filter matching.
    // Handles arrays (list metafield) and strings. Splits hyphenated values
    // so "Light-Medium" satisfies both Light and Medium filters.
    function filterTokens(field) {
      if (!field) return [];
      const values = Array.isArray(field) ? field : [field];
      const tokens = [];
      values.forEach(v => {
        String(v).split('-').forEach(t => {
          const trimmed = t.trim().toLowerCase();
          if (trimmed) tokens.push(trimmed);
        });
      });
      return tokens;
    }

    function matchesFilters(shape) {
      if (state.filters.size === 0) return true;
      const field = state.tab === 'tops' ? shape.product.support : shape.product.coverage;
      const tokens = filterTokens(field);
      if (!tokens.length) return false;
      for (const f of state.filters) {
        if (tokens.includes(f.toLowerCase())) return true;
      }
      return false;
    }

    function setText(el, val) {
      if (el) el.textContent = val || '';
    }

    // -- Tabs -------------------------------------------------------------

    function setTab(tab, opts) {
      opts = opts || {};
      state.tab = tab;
      state.filters.clear();

      tabs.forEach(t => {
        const active = t.dataset.tab === tab;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      tracks.forEach(tr => {
        tr.hidden = tr.dataset.track !== tab;
      });

      filterGroups.forEach(fg => {
        fg.hidden = fg.dataset.filterFor !== tab;
        fg.querySelectorAll('input[type="checkbox"]').forEach(cb => { cb.checked = false; });
      });

      applyFilterMuting();

      const tabShapes = shapesInTab(tab);
      if (tabShapes.length) {
        const mid = Math.floor(tabShapes.length / 2);
        selectShape(tabShapes[mid].id, opts);
      }
    }

    tabs.forEach(t => t.addEventListener('click', () => setTab(t.dataset.tab)));

    // Keyboard nav for tabs: Left/Right arrows move between tabs.
    tabs.forEach((t, idx) => {
      t.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        const dir = e.key === 'ArrowRight' ? 1 : -1;
        const next = tabs[(idx + dir + tabs.length) % tabs.length];
        if (next) {
          next.focus();
          setTab(next.dataset.tab);
        }
      });
    });

    // -- Filters ----------------------------------------------------------

    function onFilterChange() {
      const group = root.querySelector('[data-filter-for="' + state.tab + '"]');
      if (!group) return;

      state.filters.clear();
      group.querySelectorAll('input[type="checkbox"]:checked').forEach(cb => {
        state.filters.add(cb.dataset.filterValue);
      });

      applyFilterMuting();

      const current = shapesById[state.selectedId];
      if (!current || !matchesFilters(current)) {
        const first = shapesInTab(state.tab).find(matchesFilters);
        if (first) selectShape(first.id);
      }
    }

    function applyFilterMuting() {
      const track = activeTrack();
      if (!track) return;
      track.querySelectorAll('.fit-guide__shape').forEach(el => {
        const shape = shapesById[el.dataset.shapeId];
        const muted = shape && !matchesFilters(shape);
        el.dataset.muted = muted ? 'true' : 'false';
      });
    }

    root.querySelectorAll('[data-filter-for] input[type="checkbox"]').forEach(cb => {
      cb.addEventListener('change', onFilterChange);
    });

    // -- Shape selection --------------------------------------------------

    function selectShape(id, opts) {
      opts = opts || {};
      const shape = shapesById[id];
      if (!shape) return;
      state.selectedId = id;
      updateCard(shape);
      updateShapeStates();
      scrollSelectedIntoView(opts.immediate ? 'auto' : 'smooth');
    }

    function updateShapeStates() {
      const track = activeTrack();
      if (!track) return;
      const els = Array.from(track.querySelectorAll('.fit-guide__shape'));
      const selEl = els.find(el => el.dataset.shapeId === state.selectedId);
      const selIdx = selEl ? els.indexOf(selEl) : -1;

      els.forEach((el, idx) => {
        const isSel = idx === selIdx;
        el.classList.toggle('is-selected', isSel);
        const dist = selIdx >= 0 ? Math.abs(idx - selIdx) : 0;
        el.dataset.distance = isSel ? '0' : String(Math.min(dist, 3));
      });
    }

    function scrollSelectedIntoView(behavior) {
      const track = activeTrack();
      if (!track) return;
      const sel = track.querySelector('.fit-guide__shape.is-selected');
      if (!sel) return;
      const trackRect = track.getBoundingClientRect();
      const selRect = sel.getBoundingClientRect();
      const delta = (selRect.left + selRect.width / 2) - (trackRect.left + trackRect.width / 2);
      track.scrollBy({ left: delta, behavior: behavior || 'smooth' });
    }

    // -- Card -------------------------------------------------------------

    function updateCard(shape) {
      const p = shape.product || {};

      if (card.image) {
        if (p.image) {
          card.image.src = p.image;
          card.image.alt = p.title || '';
        } else {
          card.image.removeAttribute('src');
          card.image.alt = '';
        }
      }

      setText(card.title, p.title);
      setText(card.description, p.description);
      if (card.cta && p.url) card.cta.href = p.url;

      // Features: list metafield (array) or multi-line text (string).
      if (card.features) {
        card.features.innerHTML = '';
        const items = fieldToItems(p.features)
          .map(l => l.trim().replace(/^[-\u2022]\s*/, ''))
          .filter(Boolean);
        if (items.length) {
          items.forEach(line => {
            const li = document.createElement('li');
            li.textContent = line;
            card.features.appendChild(li);
          });
          card.featuresWrap.hidden = false;
        } else {
          card.featuresWrap.hidden = true;
        }
      }

      // Coverage: tops and bottoms only. One Pieces receives null from Liquid.
      if (card.coverageWrap) {
        const text = fieldToString(p.coverage);
        const show = !!text;
        card.coverageWrap.hidden = !show;
        setText(card.coverage, text);
      }

      // Cup sizes: Tops and One Pieces only.
      if (card.cupsizesWrap) {
        const text = fieldToString(p.cup_sizes);
        const show = (shape.category === 'tops' || shape.category === 'one_pieces') && !!text;
        card.cupsizesWrap.hidden = !show;
        setText(card.cupsizes, text);
      }

      // Support: Tops only.
      if (card.supportWrap) {
        const text = fieldToString(p.support);
        const show = shape.category === 'tops' && !!text;
        card.supportWrap.hidden = !show;
        setText(card.support, text);
      }
    }

    // -- Shape clicks -----------------------------------------------------

    root.querySelectorAll('.fit-guide__shape').forEach(el => {
      el.addEventListener('click', () => {
        if (el.dataset.muted === 'true') return;
        selectShape(el.dataset.shapeId);
      });
    });

    // -- Carousel nav -----------------------------------------------------

    function scrollTrack(direction) {
      const track = activeTrack();
      if (!track) return;
      const first = track.querySelector('.fit-guide__shape');
      if (!first) return;
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '16') || 16;
      const step = first.offsetWidth + gap;
      track.scrollBy({ left: direction * step * 3, behavior: 'smooth' });
    }

    if (prevBtn) prevBtn.addEventListener('click', () => scrollTrack(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => scrollTrack(1));

    // -- Init -------------------------------------------------------------
    // Default state per scope: Tops tab, no filters, middle product selected.

    setTab('tops', { immediate: true });
  }

  // Initialize on page load.
  document.querySelectorAll('[data-fit-guide]').forEach(initFitGuide);

  // Re-initialize when Shopify theme editor reloads this section.
  document.addEventListener('shopify:section:load', function (e) {
    const root = e.target.querySelector('[data-fit-guide]');
    if (root) initFitGuide(root);
  });
})();
